import type { DeviceType } from './device-type'
import type { WorkflowGraphProps } from '@google/workflow-graph'

export enum IAP_STATUS {
  ACTIVE = 'active',
  ACTIVE_2 = 'ACTIVE',
  APPROVED = 'APPROVED',
  DEVELOPER_REMOVED_FROM_SALE = 'DEVELOPER_REMOVED_FROM_SALE',
  DRAFT = 'DRAFT',
  INACTIVE = 'INACTIVE',
  INACTIVE_2 = 'inactive',
  MISSING_METADATA = 'MISSING_METADATA',
  READY_TO_SUBMIT = 'READY_TO_SUBMIT',
  REJECTED = 'rejected',
  REJECTED_2 = 'REJECTED',
  WAITING_FOR_SCREENSHOT = 'WAITING_FOR_SCREENSHOT',
}

export enum IAP_TYPE {
  PRODUCT = 'product',
  SUBSCRIPTION = 'subscription',
}

export enum IAP_SUB_TYPE {
  CONSUMABLE = 'consumable',
  NON_CONSUMABLE = 'non_consumable',
  ONE_MONTH = 'one_month',
  ONE_WEEK = 'one_week',
  ONE_YEAR = 'one_year',
  SIX_MONTHS = 'six_months',
  THREE_MONTHS = 'three_months',
}

/* eslint-disable typescript-sort-keys/string-enum */
export enum IAP_PERIODICITY {
  WEEKLY = 'weekly',
  TWO_WEEKS = '2_weeks',
  MONTHLY = 'monthly',
  TWO_MONTHS = '2_months',
  THREE_MONTHS = '3_months',
  SIX_MONTHS = '6_months',
  YEARLY = 'yearly',
}

export enum IAP_FREE_TRIAL {
  ONE_DAY = '1d',
  THREE_DAYS = '3d',
  ONE_WEEK = '1w',
  TWO_WEEKS = '2w',
  ONE_MONTH = '1m',
  TWO_MONTHS = '2m',
  THREE_MONTHS = '3m',
  SIX_MONTHS = '6m',
  ONE_YEAR = '1y',
}
/* eslint-enable typescript-sort-keys/string-enum */

export interface IIapPrice {
  country: string
  currency: string
  currency_symbol: string
  grace_period: string
  grace_period_days: number
  price: number
  price_formatted: string
  price_micros: number
  price_net: number
  price_net_after_first_year: number
  price_net_after_first_year_formatted: string
  price_net_formatted: string
  price_net_micros: number
  subscription_period: string
  subscription_period_days: number
  trial_period: string
  trial_period_days: number
}

export type IIapFeatures = Record<string, Record<string, string[]>>

export interface IIap {
  app_key: string
  app_store_id: string
  created_at?: string
  device_type: DeviceType
  id: string
  is_enable: true
  prices: {
    [key: string]: IIapPrice
  }
  sku: string
  status?: IAP_STATUS
  store_id?: string
  sub_type?: IAP_SUB_TYPE
  subscription_group_id?: string
  subscription_group_name?: string
  title: string
  type: IAP_TYPE
  unlockables?: IIapFeatures
  updated_at?: string
}

export interface IIapGraph {
  skeleton: WorkflowGraphProps['dagSpec']['skeleton']
  state: WorkflowGraphProps['dagSpec']['meta']
}

export interface IIapFeatureResource {
  features: IIapFeatures
  pack_ids: string[]
}

export interface IIapResource
  extends Pick<IIap, 'app_key' | 'device_type' | 'is_enable' | 'sku' | 'status' | 'sub_type' | 'title' | 'type'> {
  price: number
  price_formatted: string
}

///////////////////// REWORK IAP TYPES BELOW /////////////////////

export interface IInApp {
  app_key: string
  created_at: number
  default_price_usd: number
  device_type: DeviceType
  free_trial: IAP_FREE_TRIAL
  intro_offer_duration_in_days: number
  intro_offer_price_usd: number
  offers: IInAppOffer[] | null
  original_status: string
  periodicity: IAP_PERIODICITY
  prices?: IInAppPrice[]
  sku: string
  status: 'ready' | 'in_preparation' | 'rejected' | 'removed'
  store_id: string
  sub_type: 'auto_renew' | 'non_renewing' | 'intro_offer' | 'classic' | 'consumable'
  type: IAP_TYPE
}

export interface IInAppWithAppIconUrl extends IInApp {
  app_icon_url: string
}

export interface IInAppPrice extends Pick<IInApp, 'app_key' | 'device_type' | 'sku'> {
  country: string
  currency: string
  price: number
}

interface IFeature {
  app_key: string
  device_type: DeviceType
  feature_id: string
}

export interface IInAppFeature extends Pick<IInApp, 'app_key' | 'device_type'> {
  feature: IFeature
  feature_groups: IInAppFeaturesGroup[]
}

export interface IInAppFeaturesGroup extends Pick<IInApp, 'app_key' | 'device_type'> {
  feature_group_id: string
  feature_ids: string[] | null
  is_default: boolean
  name: string
}

export interface IInAppFiltersCount {
  free_trial_one_day: number | null
  free_trial_one_month: number | null
  free_trial_one_week: number | null
  free_trial_one_year: number | null
  free_trial_six_months: number | null
  free_trial_three_days: number | null
  free_trial_three_months: number | null
  free_trial_two_months: number | null
  free_trial_two_weeks: number | null
  no_free_trial: number | null
  period_monthly: number | null
  period_six_months: number | null
  period_three_months: number | null
  period_two_weeks: number | null
  period_weekly: number | null
  period_yearly: number | null
  total_products: number | null
  total_subscriptions: number | null
}

export interface IFeaturesGroupToEdit {
  featureGroupId: string
  featureIds: string[] | null
  name: string
}

export interface IOffersInApps {
  app_key: string
  device_type: string
  skus: string[]
}

export interface IInAppOffersFeatureGroup extends IInAppFeaturesGroup {
  app_key: string
  device_type: DeviceType
  name: string
}

export interface IInAppOffer {
  available_in_dev: boolean
  available_in_prod: boolean
  features_groups: IInAppOffersFeatureGroup[]
  in_apps: IInApp[]
  name: string
  offer_id: string
}

export interface IInAppOfferInList extends Omit<IInAppOffer, 'features_groups' | 'in_apps'> {
  features_groups: IInAppOffersFeatureGroup[]
  in_apps: IOffersInApps[]
}

export interface IInAppFeaturesGroupWithAppIcon extends IInAppFeaturesGroup {
  app_icon_url: string
}

//// PAYLOADS ////

export interface IApp {
  app_key: string
  device_type: DeviceType
}

export type TInAppOfferPayload = {
  apps_origin: IApp[]
  apps_unlocked: IApp[]
  features: string[]
  skus: string[]
}

export type TInAppFeaturePayload = {
  appKey: string
  deviceType: DeviceType
  featureId: string
}

export type TInAppListingPayload = {
  apps: { app_key: string; device_type: DeviceType }[]
  freeTrial: IAP_FREE_TRIAL[]
  limit: number
  page: number
  period: IAP_PERIODICITY[]
  priceRange: { max: number; min: number }
  searchQuery: string
  type?: IAP_TYPE
}

export type TInAppFiltersPayload = Omit<TInAppListingPayload, 'limit' | 'page'>

export type TInAppFeaturesGroupPayload = Omit<TInAppFeaturePayload, 'featureId'> & {
  featureIds: string[]
  name: string
}

export type TCreateOfferPayload = Omit<IInAppOffer, 'features_groups' | 'in_apps' | 'offer_id'> & {
  feature_group_ids: string[]
  in_apps: {
    app_key: string
    device_type: DeviceType
    sku: string
  }[]
}

export type TUpdateOfferPayload = TCreateOfferPayload & {
  offer_id: string
}
